/**
 * Wire types shared by the browser client and the Cloudflare Worker.
 * Everything that crosses the network is defined here.
 */

import type { Look } from './cosmetics.ts';
import type { BoxPeriod, BoxTier } from './economy.ts';
import type { LeaderboardPeriod } from './periods.ts';

export type { Look } from './cosmetics.ts';

export type Color = 'w' | 'b';
export type PromotionPiece = 'q' | 'r' | 'b' | 'n';

export interface TimeControl {
  initialMs: number;
  incrementMs: number;
}

export type GameEndReason =
  | 'checkmate'
  | 'resignation'
  | 'timeout'
  | 'timeout_vs_insufficient'
  | 'abandonment'
  | 'stalemate'
  | 'threefold_repetition'
  | 'insufficient_material'
  | 'fifty_move_rule'
  | 'agreement';

export interface GameResult {
  winner: Color | null;
  reason: GameEndReason;
}

export type RoomStatus = 'waiting' | 'playing' | 'ended' | 'closed';

export interface PublicPlayer {
  name: string;
  /** Rating at the start of a rated game; null in casual rooms. */
  rating: number | null;
  /** Avatar, frame and name style of a signed-in player; null for guests. */
  look: Look | null;
  connected: boolean;
  /** Epoch ms when the player dropped; null while connected. */
  disconnectedAt: number | null;
}

export interface PublicMove {
  san: string;
  from: string;
  to: string;
  promotion?: PromotionPiece;
}

export interface PublicRoomState {
  code: string;
  status: RoomStatus;
  gameNumber: number;
  timeControl: TimeControl | null;
  players: Record<Color, PublicPlayer | null>;
  moves: PublicMove[];
  /** Remaining time per side at `serverTime`, or null for untimed games. */
  clocks: Record<Color, number> | null;
  /** Side whose clock is ticking, if any. */
  clockRunning: Color | null;
  result: GameResult | null;
  drawOffer: Color | null;
  rematchRequests: Color[];
  reconnectGraceMs: number;
  serverTime: number;
  /** Rated games come from matchmaking and change both players' ratings. */
  rated: boolean;
  /** Rating before/after per side once a rated game is settled; null when it did not count. */
  ratingChange: Record<Color, RatingChange> | null;
  /** Created by matchmaking (rated or guest): no rematch, players search again instead. */
  matched: boolean;
  matchClock: RatedClock | null;
  /** Gold and new badges per side once a rated game is settled. */
  rewards: Record<Color, GameReward> | null;
}

export interface GameReward {
  gold: number;
  badges: string[];
}

export interface RatingChange {
  before: number;
  after: number;
}

export type Role = Color | 'spectator';

export type ClientMessage =
  | { type: 'hello'; token: string | null; name: string }
  | { type: 'move'; from: string; to: string; promotion?: PromotionPiece }
  | { type: 'resign' }
  | { type: 'offer_draw' }
  | { type: 'accept_draw' }
  | { type: 'decline_draw' }
  | { type: 'rematch' }
  | { type: 'close' };

export type ErrorCode =
  | 'not_found'
  | 'bad_request'
  | 'illegal_move'
  | 'not_your_turn'
  | 'not_a_player'
  | 'game_not_active'
  | 'invalid_action';

export type ServerMessage =
  | { type: 'welcome'; role: Role; token: string | null; state: PublicRoomState }
  /** Sent after every change; `role` can change when seats swap on rematch. */
  | { type: 'state'; role: Role; state: PublicRoomState }
  | { type: 'error'; code: ErrorCode };

export interface CreateRoomRequest {
  timeControl: TimeControl | null;
  color: Color | 'random';
  name: string;
}

export interface CreateRoomResponse {
  code: string;
  token: string;
}

export interface ApiError {
  error:
    | 'rate_limited'
    | 'bad_request'
    | 'unavailable'
    | 'unauthorized'
    | 'username_taken'
    | 'username_invalid'
    | 'username_set'
    | 'username_same'
    | 'not_enough_gold';
}

// ---------------------------------------------------------------- accounts

export interface PublicUser {
  username: string | null;
  look: Look;
  gold: number;
  bestRating: number;
  /** Gift boxes waiting to be opened. */
  boxes: number;
  rating: number;
  games: number;
  wins: number;
  losses: number;
  draws: number;
}

export interface MeResponse {
  user: PublicUser | null;
  /** Which sign-in buttons the server can serve right now. */
  login: { google: boolean; dev: boolean };
}

// ------------------------------------------------------------- matchmaking

/** Clocks offered for rated games; players only meet others who picked the same one. */
export const RATED_CLOCKS = {
  blitz: { initialMs: 5 * 60_000, incrementMs: 3_000 },
  rapid: { initialMs: 10 * 60_000, incrementMs: 0 },
} as const satisfies Record<string, TimeControl>;

export type RatedClock = keyof typeof RATED_CLOCKS;

export function isRatedClock(value: unknown): value is RatedClock {
  return typeof value === 'string' && Object.hasOwn(RATED_CLOCKS, value);
}

export type MatchServerMessage =
  | { type: 'queued'; clock: RatedClock; rated: boolean }
  | {
      type: 'matched';
      code: string;
      token: string;
      color: Color;
      opponent: { name: string; rating: number | null; look: Look | null };
    }
  /** Another tab of the same player took over the search. */
  | { type: 'replaced' };

/** Unambiguous alphabet: no 0/O, 1/I/L. */
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 6;
const ROOM_CODE_PATTERN = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);

export function normalizeRoomCode(input: string): string {
  return input.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_PATTERN.test(code);
}

export const MAX_NAME_LENGTH = 20;

export function sanitizeName(input: unknown): string {
  if (typeof input !== 'string') return '';
  // Strip control characters and collapse whitespace.
  return input
    .replace(/[\p{Cc}\p{Cf}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME_LENGTH);
}

/** Time controls offered in the lobby. Validated server-side against this list. */
export const TIME_CONTROL_PRESETS: readonly TimeControl[] = [
  { initialMs: 60_000, incrementMs: 0 },
  { initialMs: 3 * 60_000, incrementMs: 2_000 },
  { initialMs: 5 * 60_000, incrementMs: 3_000 },
  { initialMs: 10 * 60_000, incrementMs: 0 },
  { initialMs: 15 * 60_000, incrementMs: 10_000 },
  { initialMs: 30 * 60_000, incrementMs: 0 },
];

export function isPresetTimeControl(tc: TimeControl): boolean {
  return TIME_CONTROL_PRESETS.some(
    (p) => p.initialMs === tc.initialMs && p.incrementMs === tc.incrementMs,
  );
}

export function formatTimeControl(tc: TimeControl | null): string | null {
  if (!tc) return null;
  return `${tc.initialMs / 60_000}+${tc.incrementMs / 1000}`;
}

// ------------------------------------------------------- progression / shop

export interface LeaderboardEntry {
  rank: number;
  username: string;
  look: Look;
  rating: number;
  /** Rating gained in the period (day / week); null for all-time. */
  gain: number | null;
  games: number;
}

export interface LeaderboardResponse {
  period: LeaderboardPeriod;
  /** Period bounds (epoch ms); null for all-time. */
  start: number | null;
  end: number | null;
  entries: LeaderboardEntry[];
  /** The signed-in player's own row when they are ranked. */
  me: LeaderboardEntry | null;
}

export interface ProfileBadge {
  id: string;
  earnedAt: number;
}

export interface ProfileResponse {
  username: string;
  look: Look;
  rating: number;
  bestRating: number;
  games: number;
  wins: number;
  losses: number;
  draws: number;
  joinedAt: number;
  /** All-time leaderboard position, or null before the first rated game. */
  rank: number | null;
  badges: ProfileBadge[];
}

export interface InventoryResponse {
  gold: number;
  owned: string[];
  look: Look;
  /** Rewarded ads watched toward each item that ads can unlock (item id → count). */
  adProgress: Record<string, number>;
}

/** What a rewarded ad is for: plain gold, or progress toward an item. */
export type AdPurpose = 'gold' | (string & {});

export interface AdStartResponse {
  token: string;
}

export interface AdCompleteResponse extends InventoryResponse {
  /** Gold paid for this ad (0 when it counted toward an item). */
  goldEarned: number;
  /** The item this ad unlocked, if it was the last one needed. */
  unlocked: string | null;
}

export type AdError = 'bad_purpose' | 'owned' | 'too_soon' | 'not_found';

export interface GiftBox {
  id: string;
  tier: BoxTier;
  period: BoxPeriod;
  periodKey: string;
  rank: number;
  createdAt: number;
  openedAt: number | null;
  reward: { gold: number; item: string | null; duplicateGold: number } | null;
}

export interface BoxesResponse {
  boxes: GiftBox[];
}

export interface OpenBoxResponse {
  box: GiftBox;
  gold: number;
}

export type ShopError = 'not_for_sale' | 'owned' | 'not_enough_gold';

